import {
  Security as AdminIcon,
  Logout as LogOut,
  PlayArrow as PlayIcon,
  Settings,
  Person as User,
  WorkspacePremium as PlansIcon,
} from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { signOut } from 'firebase/auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tourSteps } from '../tour/dashboard-tour-steps';

import { ThemeModeToggle } from './theme-mode-toggle';
import { TokenUsageBar } from './TokenUsageBar';

import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useSubscription } from '@/hooks/useSubscription';
import { getInitials } from '@/lib/utils';

export function UserNav() {
  const { t } = useTranslation();
  const { auth, user: authUser } = useFirebase();
  const { updateProfile } = useUserProfile();
  const {
    isAdmin,
    user: dbUser,
    maxTokens,
    tokensSpent,
    tokensRemaining,
  } = useSubscription();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isTourCompleted = dbUser?.hasSeenDashboardTour === true || localStorage.getItem('eplotera_onboarding_completed') === 'true';

  const handleRestartTutorial = async () => {
    let redirectPath = '/';
    if (isTourCompleted) {
      localStorage.setItem('eplotera_onboarding_step_index', '0');
    } else {
      const storedIndex = localStorage.getItem('eplotera_onboarding_step_index');
      let index = storedIndex ? parseInt(storedIndex, 10) : 0;
      if (index > 0) {
        index = index - 1;
        localStorage.setItem('eplotera_onboarding_step_index', String(index));
      }
      const targetStep = tourSteps[index];
      if (targetStep) {
        redirectPath = targetStep.path;
      }
    }
    localStorage.removeItem('eplotera_onboarding_completed');
    if (authUser?.uid) {
      try {
        await updateProfile({
          hasSeenDashboardTour: false,
        });
      } catch (error) {
        console.error("Failed to reset tour state in Firestore:", error);
      }
    }
    window.location.href = redirectPath;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    handleClose();
    signOut(auth);
  };

  if (!dbUser && !authUser) {
    return null;
  }

  const displayName = dbUser?.displayName || authUser?.displayName;
  const photoURL = dbUser?.photoURL || authUser?.photoURL;
  const email = dbUser?.email || authUser?.email;
  const planId = dbUser?.planId || 'free';
  const isFreeUser = Boolean(dbUser) && (!dbUser?.planId || dbUser.planId === 'free');
  return (
    <>
      {isFreeUser && (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => { window.location.href = '/pricing'; }}
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            mr: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          {t('pricing.upgrade')}
        </Button>
      )}
      <Box
        id="user-nav-avatar-btn"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        {maxTokens > 0 && (
          <Box sx={{ display: 'block', mx: { xs: 0.5, md: 1 } }}>
            <TokenUsageBar
              spent={tokensSpent}
              max={maxTokens}
              remaining={tokensRemaining}
            />
          </Box>
        )}
        <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
            {displayName ?? t('nav.anonymous_user')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
            {t(`pricing.plans.${planId}.name`)} {t('nav.author')}
          </Typography>
        </Box>
        <Avatar
          src={photoURL ?? ''}
          alt={displayName ?? t('nav.user_alt')}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.875rem',
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            border: '2px solid',
            borderColor: 'divider',
          }}
        >
          {getInitials(displayName) || <User sx={{ fontSize: 20 }} />}
        </Avatar>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mb: 1.5,
            width: 220,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(50%) rotate(45deg)',
              borderBottom: '1px solid',
              borderRight: '1px solid',
              borderColor: 'divider',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {displayName ?? t('nav.anonymous_user')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {email ?? t('nav.no_email')}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => { handleClose(); window.location.href = '/profile'; }} sx={{ py: 1, fontSize: '0.875rem' }}>
          <ListItemIcon>
            <User sx={{ fontSize: 18 }} />
          </ListItemIcon>
          {t('nav.profile')}
        </MenuItem>
        <ThemeModeToggle placement="account-menu" />
        {isFreeUser && (
          <MenuItem
            onClick={() => { handleClose(); window.location.href = '/pricing'; }}
            sx={{ py: 1, fontSize: '0.875rem', color: 'primary.main' }}
          >
            <ListItemIcon>
              <PlansIcon sx={{ fontSize: 18, color: 'inherit' }} />
            </ListItemIcon>
            {t('nav.view_plans')}
          </MenuItem>
        )}
        <MenuItem disabled onClick={handleClose} sx={{ py: 1, fontSize: '0.875rem' }}>
          <ListItemIcon>
            <Settings sx={{ fontSize: 18 }} />
          </ListItemIcon>
          {t('nav.settings')}
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); handleRestartTutorial(); }} sx={{ py: 1, fontSize: '0.875rem' }}>
          <ListItemIcon>
            <PlayIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          {isTourCompleted
            ? t('tour.actions.restart_tutorial')
            : t('tour.actions.resume_tutorial')}
        </MenuItem>
        {isAdmin && (
          <MenuItem
            onClick={() => { handleClose(); window.location.href = '/adeptus-administratum'; }}
            sx={{ py: 1, fontSize: '0.875rem', color: 'primary.main' }}
          >
            <ListItemIcon>
              <AdminIcon sx={{ fontSize: 18, color: 'inherit' }} />
            </ListItemIcon>
            {t('nav.admin_panel')}
          </MenuItem>
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={handleSignOut} sx={{ py: 1, fontSize: '0.875rem', color: 'error.main' }}>
          <ListItemIcon>
            <LogOut sx={{ fontSize: 18, color: 'inherit' }} />
          </ListItemIcon>
          {t('nav.log_out')}
        </MenuItem>
      </Menu>
    </>
  );
}
